import React from 'react';
import { PCAReportMetric } from 'c-sdk';
import { AutoGrid, HighlightWord, NumberFormat, NumberFormatWhole } from 'c-components';
import { Box, IconButton, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import SetCampaignNotices from 'c-main/Components/Campaign/SetCampaignNotices';
import HoursMins from 'c-main/Components/Campaign/CampaignReports/HoursMins';

export const LeaderboardRows = (
    data: any[],
    {
        campaignId,
        fieldLabel,
        filterParts,
        metric,
        field,
        actualLabel,
        expectedLabel,
        inScheduleLabel,
        outScheduleLabel,
        unbookedLabel,
        showPerformance,
        showInSchedule,
        showOutSchedule,
        showUnbooked,
        onOpenField,
        columnOrder,
        performanceLabel,
        varianceLabel,
    }: {
        campaignId: number;
        fieldLabel: string;
        filterParts: string[];
        metric: PCAReportMetric;
        field: string;
        actualLabel: string;
        expectedLabel: string;
        inScheduleLabel: string;
        outScheduleLabel: string;
        unbookedLabel: string;
        showPerformance: boolean;
        percentageLabel: string;
        showInSchedule: boolean;
        showOutSchedule: boolean;
        showUnbooked: boolean;
        onOpenField: (field: string) => void;
        columnOrder: string[];
        performanceLabel: string;
        varianceLabel: string;
    },
) =>
    data.map((dataRow, dataIndex) => {
        const fieldLabelText: string = dataRow[fieldLabel] ?? '';
        const rawFieldValue: string = dataRow.name ?? fieldLabelText;
        const matchedParts: string[] = filterParts.filter(
            p => fieldLabelText.toLowerCase().indexOf(p) !== -1,
        );
        const longestMatchedPart = matchedParts.reduce(
            (acc, curr) => (curr.length > acc.length ? curr : acc),
            '',
        );

        // prebuilt cells for reuse
        const actualContent =
            metric === PCAReportMetric.Time ? (
                <HoursMins
                    seconds={dataRow[actualLabel]}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            ) : (
                <NumberFormatWhole value={dataRow[actualLabel]} />
            );

        const expectedContent =
            metric === PCAReportMetric.Time ? (
                <HoursMins
                    seconds={dataRow[expectedLabel]}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            ) : (
                <NumberFormatWhole value={dataRow[expectedLabel]} />
            );

        const inScheduleContent =
            metric === PCAReportMetric.Time ? (
                <HoursMins
                    seconds={dataRow[inScheduleLabel]}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            ) : (
                <NumberFormatWhole value={dataRow[inScheduleLabel]} />
            );

        const outScheduleContent =
            metric === PCAReportMetric.Time ? (
                <HoursMins
                    seconds={dataRow[outScheduleLabel]}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            ) : (
                <NumberFormatWhole value={dataRow[outScheduleLabel]} />
            );

        const unbookedContent =
            metric === PCAReportMetric.Time ? (
                <HoursMins
                    seconds={dataRow[unbookedLabel]}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            ) : (
                <NumberFormatWhole value={dataRow[unbookedLabel]} />
            );

        const perfValue = Number(dataRow[performanceLabel]);
        const hasExpected = Number(dataRow[expectedLabel]) > 0;

        const performanceColor = () => {
            if (!showPerformance || !hasExpected) return undefined;
            return perfValue >= 100 ? 'success.main' : 'error.main';
        };

        const performanceContent = () => {
            if (!hasExpected) return <>-</>;
            return (
                <Box color={performanceColor()}>
                    <NumberFormat value={perfValue} />%
                </Box>
            );
        };

        const varianceValue = Number(dataRow[varianceLabel]);
        const varianceSign = Number.isFinite(varianceValue)
            ? varianceValue > 0
                ? '+'
                : varianceValue < 0
                ? '-'
                : ''
            : '';
        const varianceAbs = Math.abs(varianceValue);
        const varianceColor =
            varianceValue > 0 ? 'success.main' : varianceValue < 0 ? 'error.main' : undefined;

        const varianceContent = !Number.isFinite(varianceValue) ? (
            <>-</>
        ) : metric === PCAReportMetric.Time ? (
            <Box component="span" sx={{ color: varianceColor, display: 'inline-flex', gap: 0.5 }}>
                {varianceSign}
                <HoursMins
                    seconds={varianceAbs}
                    variant="body2"
                    alwaysShowMinutes
                    showSecondsIfNoHours
                />
            </Box>
        ) : (
            <Box component="span" sx={{ color: varianceColor }}>
                {varianceSign}
                <NumberFormatWhole value={varianceAbs} />
            </Box>
        );

        // Field cell (Partner + actions)
        const fieldCell = (
            <AutoGrid
                spacing={0}
                sx={{ alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'space-between' }}
            >
                <Typography
                    variant="body1"
                    sx={{ maxWidth: '25rem', minWidth: '10rem', wordBreak: 'break-word' }}
                >
                    <HighlightWord searchTerm={longestMatchedPart} text={fieldLabelText} />
                </Typography>

                <Box display="flex" flexWrap="nowrap">
                    {dataRow.id != null && (
                        <SetCampaignNotices
                            campaignId={campaignId}
                            field={field}
                            id={dataRow.id}
                            name={rawFieldValue}
                        />
                    )}
                    <IconButton onClick={() => onOpenField(rawFieldValue)}>
                        <InfoOutlined />
                    </IconButton>
                </Box>
            </AutoGrid>
        );

        // Render by columnOrder
        const renderByColumn = (col: string) => {
            switch (col) {
                case fieldLabel:
                    return fieldCell;
                case performanceLabel:
                    return showPerformance ? performanceContent() : undefined;
                case expectedLabel:
                    return expectedContent;
                case actualLabel:
                    return actualContent;
                case inScheduleLabel:
                    return showInSchedule ? inScheduleContent : undefined;
                case outScheduleLabel:
                    return showOutSchedule ? outScheduleContent : undefined;
                case unbookedLabel:
                    return showUnbooked ? unbookedContent : undefined;
                case varianceLabel:
                    return varianceContent;
                default:
                    return dataRow[col] ?? undefined;
            }
        };

        return {
            content: [dataIndex + 1, ...columnOrder.map(col => renderByColumn(col))]
                .filter(c => c != null)
                .map(c => ({ content: c })),
        };
    });
